import { doc, setDoc, collection, query, where, getDocs, limit, updateDoc, getDoc as getFirestoreDoc, orderBy, Timestamp } from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { db, storage, firestorePathId } from './firebaseConfig';
import { FirebaseError } from "firebase/app"; // para teste de erros

export const getAcessoServidorByCPF = async (municipioId, cpf) => {
    try {
        const cleanedCpf = cpf.replace(/[.-]/g, '');
        const docPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/Servidoresacesso/${cleanedCpf}`;
        const docRef = doc(db, docPath);
        const docSnap = await getFirestoreDoc(docRef);

        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
        } else {
            console.warn(`[Firebase] Nenhum documento de acesso encontrado para o CPF: ${cleanedCpf}`);
            return null;
        }
    } catch (error) {
        console.error("Erro ao buscar acesso do servidor por CPF:", error);
        throw error;
    }
};

export const getVinculosByCPF = async (municipioId, cpf, uid) => {
    if (!municipioId || !cpf || !uid) return [];
    try {
        const cleanedCpf = cpf.replace(/[.-]/g, '');
        const collectionPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/DadosServidores`;
        const q = query(collection(db, collectionPath), where("doc_cpf", "==", cleanedCpf));
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
            console.warn(`[Firebase] Nenhum vínculo de dados encontrado para o CPF: ${cleanedCpf}`);
            return [];
        }
        
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), vinculo_id: doc.data().vinculo_id }));
    } catch (error) {
        console.error("Erro ao buscar vínculos por CPF:", error);
        return [];
    }
};

export const getDadosServidorByCPF = async (municipioId, cpf, vinculoId) => {
    try {
        if (!municipioId || !cpf || vinculoId === undefined) {
            console.warn("getDadosServidorByCPF: IDs de município, CPF ou vínculo não fornecidos.");
            return null;
        }
        const cleanedCpf = cpf.replace(/[.-]/g, '');
        const docId = `${cleanedCpf}-${vinculoId}`;
        const docRef = doc(db, `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/DadosServidores`, docId);
        const docSnap = await getFirestoreDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            console.warn(`Nenhum dado de servidor encontrado para o CPF: ${cleanedCpf} e Vínculo: ${vinculoId}`);
            return null;
        }
    } catch (error) {
        console.error("Erro ao buscar dados do servidor por CPF e Vínculo:", error);
        throw error;
    }
};

export const updateServerPassword = async (municipioId, cpf, newPassword) => {
    try {
        const cleanedCpf = cpf.replace(/[.-]/g, '');
        const credentialPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/Servidoresacesso/${cleanedCpf}`;
        const credentialDocRef = doc(db, credentialPath);
        await updateDoc(credentialDocRef, {
            senha: newPassword,
            primeiroAcesso: false,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error("Erro ao atualizar a senha do servidor:", error);
        throw error;
    }
};

export const updateAcessoWithUID = async (municipioId, cpf, uid) => {
    try {
        const cleanedCpf = cpf.replace(/[.-]/g, '');
        const docPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/Servidoresacesso/${cleanedCpf}`;
        const docRef = doc(db, docPath);
        await updateDoc(docRef, { uid });
    } catch (error) {
        console.error("Erro ao atualizar o documento de acesso com o UID:", error);
        throw error;
    }
};

/**
 * Realiza o upload de um arquivo para o Firebase Storage.
 */
export const uploadFile = async (file, path) => {
    try {
        if (!file || !path) {
            throw new Error("Arquivo ou caminho de upload não fornecidos.");
        }
        const storageRef = ref(storage, path);
        
        const metadata = {
            contentType: file.type
        };

        const snapshot = await uploadBytes(storageRef, file, metadata);
        return snapshot.ref.fullPath;
    } catch (error) {
        console.error("Erro detalhado ao fazer upload do arquivo:", error.message, error.stack);
        throw error;
    }
};

export const saveRecadastramento = async (municipioId, data, user) => {
    if (!municipioId || !data || !data.campanhaId || !data.id || !user || !user.uid || !user.cpf) {
        const errorMessage = "Dados insuficientes para salvar. É necessário 'municipioId', 'data', 'data.campanhaId', 'data.id', 'user.uid' e 'user.cpf'.";
        console.error(errorMessage, { data, user });
        throw new Error(errorMessage);
    }

    try {
        const docId = data.id;
        const cleanedUserCpf = user.cpf.replace(/[.-]/g, '');

        const dataToSave = {
            ...data,
            userId: user.uid,
            userCpf: cleanedUserCpf,
        };

        const docPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/recadastramentos/${data.campanhaId}/servidores/${docId}`;
        const docRef = doc(db, docPath);

        await setDoc(docRef, dataToSave, { merge: true });
        
        return docId;
    } catch (error) {
        console.error("Erro ao salvar dados do recadastramento no Firestore:", error);
        throw error;
    }
};

export const getRecadastramentoByCPF = async (municipioId, cpf) => {
    try {
        if (!cpf || !municipioId) {
            console.warn("getRecadastramentoByCPF: cpf ou municipioId inválido/ausente.");
            return null;
        }
        
        const cleanedCpf = cpf.replace(/[.-]/g, '');

        const activeCampaign = await getActiveCampaignRequiredFields(municipioId);
        if (!activeCampaign) {
            console.warn(`Nenhuma campanha ativa encontrada para o município ${municipioId}.`);
            return null;
        }
        
        const collectionPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/recadastramentos/${activeCampaign.id}/servidores`;
        
        const q = query(collection(db, collectionPath), where("userCpf", "==", cleanedCpf), limit(1));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const docSnap = querySnapshot.docs[0];
            return { id: docSnap.id, ...docSnap.data() };
        } else {
            return null;
        }
    } catch (error) {
        console.error("Erro ao buscar recadastramento por CPF na campanha ativa:", error);
        throw error;
    }
};

export const getActiveCampaignRequiredFields = async (municipioId) => {
    try {
        const campaignsCollectionRef = collection(db, `artifacts/${firestorePathId}/public/data/recadastramentoCampaigns`);
        const q = query(
            campaignsCollectionRef,
            where('status', '==', 'ativa'),
            where('municipioId', '==', municipioId),
            limit(1)
        );
        
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
            return { id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() };
        } else {
            return null;
        }
    } catch (error) {
        console.error("ERRO CRÍTICO ao buscar campanha ativa:", error);
        throw error;
    }
};

export const updateRecadastramentoStatus = async (municipioId, campanhaId, cpf, newStatus) => {
    try {
        if (!municipioId || !campanhaId || !cpf || !newStatus) {
            throw new Error("municipioId, campanhaId, cpf e newStatus devem ser fornecidos.");
        }
        const cleanedCpf = cpf.replace(/[.-]/g, '');
        const collectionPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/recadastramentos/${campanhaId}/servidores`;
        
        const q = query(collection(db, collectionPath), where("userCpf", "==", cleanedCpf), limit(1));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            throw new Error(`Nenhum recadastramento encontrado para o CPF ${cleanedCpf} na campanha ${campanhaId}.`);
        }
        
        const docRef = querySnapshot.docs[0].ref;

        await updateDoc(docRef, {
            status: newStatus,
            lastUpdate: new Date(),
        });
    } catch (error) {
        console.error("Erro ao atualizar o status do recadastramento:", error);
        throw error;
    }
};

export const getMunicipioById = async (municipioId) => {
    try {
        if (!municipioId) {
            console.warn("getMunicipioById: municipioId não fornecido.");
            return null;
        }
        const docRef = doc(db, `artifacts/${firestorePathId}/public/data/municipalities`, municipioId);
        const docSnap = await getFirestoreDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            console.warn("Nenhum documento de município encontrado para o ID:", municipioId);
            return null;
        }
    } catch (error) {
        console.error("Erro ao buscar dados do município:", error);
        throw error;
    }
};

export const getRhUserById = async (rhUserId) => {
    try {
        if (!rhUserId) {
            console.warn("getRhUserById: rhUserId não fornecido.");
            return null;
        }
        const docRef = doc(db, `artifacts/${firestorePathId}/public/data/rhUsers`, rhUserId);
        const docSnap = await getFirestoreDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            console.warn("Nenhum usuário RH encontrado para o ID:", rhUserId);
            return null;
        }
    } catch (error) {
        console.error("Erro ao buscar dados do usuário RH:", error);
        throw error;
    }
};

// ===== INÍCIO DA FUNÇÃO DE CAMPANHAS DE RECADASTRAMENTO =====
export const getCampaignsByMunicipio = async (municipioId) => {
    try {
        const campaignsCollectionRef = collection(db, `artifacts/${firestorePathId}/public/data/recadastramentoCampaigns`);
        const q = query(
            campaignsCollectionRef,
            where('municipioId', '==', municipioId)
        );
        
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
            return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } else {
            return [];
        }
    } catch (error) {
        console.error("Erro ao buscar campanhas por município:", error);
        throw error;
    }
};
// ===== FIM DA FUNÇÃO DE CAMPANHAS DE RECADASTRAMENTO =====

// ===== INÍCIO DA FUNÇÃO DE HISTÓRICO DE RECADASTRAMENTO =====
export const getHistoricoRecadastramento = async (municipioId, cpf) => {
    try {
        const campaigns = await getCampaignsByMunicipio(municipioId);
        if (!campaigns || campaigns.length === 0) {
            return [];
        }

        const cleanedCpf = cpf.replace(/[.-]/g, '');
        
        const historicoPromises = campaigns.map(async (campaign) => {
            if (!campaign.id) return null;
            
            const collectionPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/recadastramentos/${campaign.id}/servidores`;
            
            const q = query(
                collection(db, collectionPath), 
                where("userCpf", "==", cleanedCpf), 
                where("status", "==", "aceito"), 
                limit(1)
            );
            
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
                const docSnap = querySnapshot.docs[0];
                return { 
                    ...docSnap.data(), 
                    id: docSnap.id,
                    campanhaId: campaign.id,
                    campanhaNome: campaign.name || `Campanha (ID: ${campaign.id})`
                };
            }
            
            return null;
        });

        const historicoResultados = await Promise.all(historicoPromises);
        const historicoFinal = historicoResultados.filter(h => h !== null);
        return historicoFinal;

    } catch (error) {
        console.error("Erro ao buscar histórico de recadastramento:", error);
        throw error;
    }
};
// ===== FIM DA FUNÇÃO DE HISTÓRICO DE RECADASTRAMENTO =====

// ===== INÍCIO DA NOVA FUNÇÃO (AVISOS) =====
/**
 * Busca os avisos ativos e públicos para um município específico.
 * Ordena por data de criação (mais recentes primeiro).
 * * @param {string} municipioId O ID do município.
 * @returns {Promise<Array<Object>>} Uma lista de documentos de aviso.
 */
export const getAvisosAtivos = async (municipioId) => {
    try {
        if (!municipioId) {
            console.warn("getAvisosAtivos: municipioId não fornecido.");
            return [];
        }

        const collectionPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/avisos`;
        const avisosCollectionRef = collection(db, collectionPath);
        
        // Pega a data/hora atual para filtrar
        const agora = Timestamp.now();

        // Constrói a query:
        // 1. Apenas avisos 'publicado'
        // 2. Apenas avisos cuja data de expiração é maior que agora
        // 3. Ordena pelos mais novos (dataCriacao descendente)
        // Lembre-se: esta query requer um índice composto no Firestore.
        const q = query(
            avisosCollectionRef, 
            where('status', '==', 'publicado'),
            where('dataExpiracao', '>', agora),
            orderBy('dataCriacao', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
            return [];
        }
        
        // Retorna os dados mapeados com o ID do documento
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    } catch (error) {
        console.error("Erro ao buscar avisos ativos:", error);
        if (error.code === 'failed-precondition') {
            console.error(
                "Firestore 'failed-precondition': Esta consulta provavelmente requer um índice composto. " +
                "Verifique o console de erros do Firebase para o link de criação do índice."
            );
        }
        throw error;
    }
};
// ===== FIM DA NOVA FUNÇÃO (AVISOS) =====


// ---------------------------
// Funções auxiliares para CONTRACHEQUE (ATUALIZADAS COM FALLBACK)
// ---------------------------

/**
 * Faz fetch de um JSON público hospedado em /artifacts/...
 * Retorna null se não existir ou houver erro.
 */
async function fetchPublicArtifactJson(path) {
  try {
    if (!path) return null;
    const base = '/'; // ajuste se seu hosting usa prefixo
    const res = await fetch(base + path, { cache: 'no-store' });
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.warn("fetchPublicArtifactJson erro:", err);
    return null;
  }
}

/**
 * getCompetenciasVisiveis: tenta a query ideal; se falhar, tenta listar sem filtro; se falhar, tenta arquivo público
 */
export const getCompetenciasVisiveis = async (municipioId) => {
    try {
        if (!municipioId) {
            console.warn("getCompetenciasVisiveis: municipioId não fornecido.");
            return [];
        }

        const collectionPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/contracheque`;
        const collectionRef = collection(db, collectionPath);

        // 1) tentativa ideal: query por visivelParaServidor + orderBy
        try {
            const q = query(collectionRef, where('visivelParaServidor', '==', true), orderBy('competencia', 'desc'));
            const snap = await getDocs(q);

            if (!snap.empty) {
                const comps = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
                comps.sort((a,b) => (b.id||'').localeCompare(a.id||''));
                return comps;
            }
            return [];
        } catch (errQuery) {
            console.warn("Query por competências falhou (vai tentar fallback):", errQuery);

            // 2) tentativa alternativa: listar a coleção inteira e filtrar cliente-side
            try {
                const snapAll = await getDocs(collectionRef);
                if (!snapAll.empty) {
                    const all = snapAll.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
                    const comps = all.filter(x => x && x.visivelParaServidor === true)
                                 .sort((a,b) => (b.id||'').localeCompare(a.id||''));
                    return comps;
                }
                return [];
            } catch (errList) {
                console.warn("Tentativa de listar coleção sem filtro também falhou:", errList);
                // 3) fallback final: tentar arquivo público (se existir)
                const path = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/contracheque/competencias.json`;
                const json = await fetchPublicArtifactJson(path);
                if (json && Array.isArray(json)) {
                    const comps = json.map(item => ({ id: item.id || item.competencia || '', label: item.label || item.id || item.competencia }));
                    comps.sort((a,b) => (b.id||'').localeCompare(a.id||''));
                    return comps;
                }
                if (errQuery instanceof FirebaseError) throw errQuery;
                throw errList;
            }
        }
    } catch (finalErr) {
        console.error("getCompetenciasVisiveis erro definitivo:", finalErr);
        throw finalErr;
    }
};

/**
 * getContrachequeServidor: tenta get(doc); se permission-denied tenta buscar JSON público.
 */
export const getContrachequeServidor = async (municipioId, competencia, cpf, matricula) => {
    try {
        if (!municipioId || !competencia || !cpf || !matricula) {
            console.warn("getContrachequeServidor: argumentos insuficientes.", { municipioId, competencia, cpf, matricula });
            return null;
        }

        const cleanedCpf = String(cpf).replace(/[.-]/g, '');
        const docId = `${cleanedCpf}-${matricula}`;
        const docPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/contracheque/${competencia}/servidores/${docId}`;
        const docRef = doc(db, docPath);

        try {
            const snap = await getFirestoreDoc(docRef);
            if (snap.exists()) {
                const data = snap.data() || {};
                data.rubricas = Array.isArray(data.rubricas) ? data.rubricas : [];
                return { id: snap.id, ...data };
            }
            return null;
        } catch (errDoc) {
            console.warn("getContrachequeServidor: get doc falhou, tentando fallback público:", errDoc);
            // se for permissão, tenta JSON público
            if (errDoc instanceof FirebaseError && errDoc.code === 'permission-denied') {
                const filePath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/contracheque/${competencia}/servidores/${docId}.json`;
                const json = await fetchPublicArtifactJson(filePath);
                if (json) {
                    json.rubricas = Array.isArray(json.rubricas) ? json.rubricas : [];
                    return json;
                }
            }
            // rethrow outros erros
            throw errDoc;
        }
    } catch (finalErr) {
        console.error("getContrachequeServidor erro definitivo:", finalErr);
        throw finalErr;
    }
};